import AgentIcon from "../AgentIcon";
import { AgentChatExtras } from "./AgentChatMessage";
import AgentChatMessage from "./AgentChatMessage";
import UserChatMessage from "./UserChatMessage";
import type { AgentExtraItem } from "@app/config/agentWebSocket";
import type { ConnectionState } from "@app/config/agentWebSocket";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useFeatureFlag } from "@app/hooks/useFeatureFlag";
import type { AgentChatMode, AgentChatSettings } from "@app/state/types";
import type { ChatMessage, CommittedDocuments } from "@lib/agent/types";
import Icon from "@lib/common/components/Icon/Icon";
import formatChatDate from "@lib/common/utils/formatChatDate";
import { useScrollPin } from "@lib/experiment/agent/AgentChat/useScrollPin";
import ChatBox, { type ChatBoxSubmitPayload } from "@lib/experiment/agent/ExperimentDesignChatBox/ChatBox";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Menu,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  Transition
} from "@mantine/core";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

export type AgentChatProps = {
  messages: ChatMessage[];
  extras?: AgentExtraItem[];
  isStreaming: boolean;
  /** True from the click of Stop until the turn actually settles — drives the Stop button's
   * spinner/disabled state while cooperative backend cancellation catches up. */
  isStopping: boolean;
  connectionState: ConnectionState;
  /** Sends a message over the websocket. Used for in-message quick-replies, which never carry
   * attachments — the compose area's upload-then-send path is `onComposerSubmit`. */
  send: (content: string, attachments?: { filenames?: string[] }) => void;
  /** Compose-area submit: uploads any draft files, then sends. Returns `false` to keep the draft
   * (e.g. an upload failed) so ChatBox doesn't clear the text and files. */
  onComposerSubmit: (message: string, payload: ChatBoxSubmitPayload) => Promise<boolean | void>;
  /** Draft files held in the compose area, not yet uploaded. */
  selectedFiles: File[];
  onFilesChange: (files: File[]) => void;
  /** Documents already committed to the session — count caps the picker slots; totalBytes feeds the
   * cumulative size hint. Both are soft hints: the upload endpoint stays authoritative. */
  committedDocuments: CommittedDocuments;
  stop: VoidFunction;
  /** True while the persisted message history is loading on first mount. */
  isHistoryLoading?: boolean;
  /** True when the history bootstrap query failed; renders an inline retry banner. */
  historyError?: boolean;
  disabledClear?: boolean;
  userFeedback?: ReactNode;
  onRetryHistory?: () => void;
  onClear: VoidFunction;
  variant?: "draft" | "experiment";
  /** True while session documents are uploading (bootstrap handoff or compose path). */
  isUploading?: boolean;
  composerSeed?: string;
  onComposerSeedConsumed?: () => void;
} & Pick<AgentChatSettings, "chatMode"> & {
    setChatMode: (mode: AgentChatMode) => void;
    toggle: (value: boolean) => void;
  };

export default function AgentChat({
  messages,
  extras,
  isStreaming,
  isStopping,
  connectionState,
  send,
  onComposerSubmit,
  selectedFiles,
  onFilesChange,
  committedDocuments,
  stop,
  isHistoryLoading = false,
  historyError = false,
  onRetryHistory,
  onClear,
  disabledClear,
  userFeedback,
  variant = "experiment",
  chatMode,
  setChatMode,
  toggle,
  isUploading = false,
  composerSeed,
  onComposerSeedConsumed
}: AgentChatProps) {
  const posthog = usePostHog();
  const isReady = connectionState === "connected";
  const isReconnecting = connectionState === "reconnecting";
  const fileUploadEnabled = useFeatureFlag("agent-file-upload");

  const viewportRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const { scrollToBottom: pinToBottom, showScrollToBottom } = useScrollPin(viewportRef, messages);

  const scrollToBottom = useCallback(() => {
    pinToBottom();
    chatInputRef.current?.focus();
  }, [pinToBottom]);

  // Scroll to the bottom the first time messages populate so history loads at the end.
  const hasInitialScrolledRef = useRef(false);
  useEffect(() => {
    if (hasInitialScrolledRef.current || messages.length === 0) {
      return;
    }
    hasInitialScrolledRef.current = true;
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus chat input when connected
  useEffect(() => {
    if (isReady) {
      chatInputRef.current?.focus();
      scrollToBottom();
    }
  }, [isReady, scrollToBottom]);

  const handleSend = useCallback(
    (message: string, payload: ChatBoxSubmitPayload) => {
      posthog.capture(posthogEvents.experiment_agent.agent_chat_input_submit, { text_length: message.length });
      // Must return (not fire-and-forget) the result: ChatBox awaits it to decide whether to clear
      // the composer, so swallowing it here would always clear the draft even on a failed send.
      return onComposerSubmit(message, payload);
    },
    [posthog, onComposerSubmit]
  );

  const handleMinimize = useCallback(() => {
    posthog.capture(posthogEvents.experiment_agent.minimize_click);
    toggle(false);
  }, [posthog, toggle]);

  const handleSetChatMode = useCallback(
    (mode: AgentChatMode) => {
      posthog.capture(posthogEvents.experiment_agent.toggle_chat_mode_click, { mode });
      setChatMode(mode);
    },
    [posthog, setChatMode]
  );

  const handleAttachClick = useCallback(() => {
    posthog.capture(posthogEvents.experiment_agent.file_upload_click);
  }, [posthog]);

  const formattedDate = useMemo(() => formatChatDate(new Date()), []);

  const defaultPlaceholder = variant === "draft" ? "Describe your experiment..." : "Ask about this experiment...";
  // Hint the input with the most recent open question's placeholder, if any. The action check
  // narrows the union, so input_placeholder is reachable without a cast.
  const inputSuggestion = (extras ?? []).reduce<string | null>(
    (acc, item) => (item.action === "question_open" ? (item.input_placeholder ?? null) : acc),
    null
  );

  // `isUploading` isn't folded in here: ChatBox already gates on it internally (re-entry guard,
  // dimmed compose area), so adding it here would just be a second source of truth for the same
  // "busy" state.
  const disabled = !isReady || isHistoryLoading || historyError;
  const showEmptyState = !isHistoryLoading && !historyError && messages.length === 0;

  return (
    <Stack h="100%" gap={0}>
      <Group px="xs" align="center" gap="md" justify="space-between" pb={5} wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <AgentIcon size="1rem" />
          <Stack gap={0}>
            <Text size="xs" fw={500} lh={1} truncate>
              AI chat · {formattedDate}
            </Text>
            {isReconnecting && (
              <Text size="xs" c="orange" lh={1.4}>
                Reconnecting…
              </Text>
            )}
          </Stack>
        </Group>
        <Group gap={5} wrap="nowrap">
          <Tooltip label="Clear conversation" disabled={disabledClear}>
            <ActionIcon onClick={onClear} disabled={disabledClear}>
              <Icon name="trash" size="0.9rem" />
            </ActionIcon>
          </Tooltip>

          <Menu width={150}>
            <Menu.Target>
              <Tooltip label="Switch chat mode">
                <ActionIcon>
                  <Icon name="sidebar-flip" size="1rem" />
                </ActionIcon>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => handleSetChatMode("sidebar")}
                leftSection={<Icon name="sidebar-flip" color="dimmed" size="0.8rem" />}
                rightSection={chatMode === "sidebar" ? <Icon name="check" /> : undefined}
              >
                Sidebar
              </Menu.Item>
              <Menu.Item
                onClick={() => handleSetChatMode("floating")}
                leftSection={<Icon name="window-restore" color="dimmed" size="0.8rem" />}
                rightSection={chatMode === "floating" ? <Icon name="check" /> : undefined}
              >
                Floating
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Tooltip label="Hide chat">
            <ActionIcon onClick={handleMinimize}>
              <Icon name="dash" size="0.8rem" variant="solid" />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      <ScrollArea viewportRef={viewportRef} flex={1} scrollbarSize={2}>
        <Box p="sm" pb="5rem" pr="sm">
          <Stack gap="md" py="md" style={{ display: "flex", flexDirection: "column" }}>
            {historyError && (
              <Center>
                <Stack gap="sm" align="center" mt="lg" maw={400}>
                  <Text c="dimmed" ta="center">
                    Couldn&apos;t load the conversation history.
                  </Text>
                  {onRetryHistory && (
                    <Button variant="light" size="xs" onClick={onRetryHistory}>
                      Retry
                    </Button>
                  )}
                </Stack>
              </Center>
            )}
            {isHistoryLoading && (
              <Stack gap="md" py="md">
                <Skeleton height={48} width="60%" radius="md" />
                <Skeleton height={64} width="80%" radius="md" ml="auto" />
                <Skeleton height={48} width="55%" radius="md" />
              </Stack>
            )}
            {showEmptyState && (
              <Center>
                <Box mt="20vh" maw={650}>
                  <Stack gap="md">
                    {variant === "draft" ? (
                      <Stack gap="sm">
                        <Text style={{ textWrap: "balance" }}>
                          Describe your experiment and I&apos;ll fill in the draft next to this chat.
                          <br />
                          <br />
                          You can also edit any field by hand — we stay in sync.
                          <br />
                          <br />
                          Click <b>Create experiment</b> when you&apos;re ready.
                        </Text>
                      </Stack>
                    ) : (
                      <Stack gap="sm">
                        <Text style={{ textWrap: "balance" }}>
                          Ask about this experiment&apos;s recommendations, insights, status, expert context, etc.
                          <br />
                          <br />I can also help you reconfigure the experiment, like updating the expert context,
                          modifying variables and results, adjusting parallelization, or phrase a good description.
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Center>
            )}
            {messages.map((message, i) =>
              message.role === "user" ? (
                <UserChatMessage message={message} key={message.id} />
              ) : (
                <AgentChatMessage
                  key={message.id}
                  message={message}
                  isStreaming={isStreaming && i === messages.length - 1}
                />
              )
            )}
            {extras && extras.length > 0 && <AgentChatExtras extras={extras} onSend={send} />}
          </Stack>
        </Box>
      </ScrollArea>
      {userFeedback ? <Center mb={5}>{userFeedback}</Center> : undefined}
      <Box px="xs" pos="relative">
        <Transition mounted={showScrollToBottom} transition="pop" enterDelay={200}>
          {transitionStyles => (
            <Center pos="absolute" left={0} right={0} top={-60}>
              <ActionIcon
                style={{
                  ...transitionStyles,
                  boxShadow: "var(--mantine-shadow-lg)",
                  border: "solid 1px var(--mantine-color-default-border)"
                }}
                radius="xl"
                onClick={scrollToBottom}
                bg="var(--mantine-color-default)"
              >
                <Icon name="arrow-down" size="0.8rem" color="dimmed" />
              </ActionIcon>
            </Center>
          )}
        </Transition>

        <ChatBox
          ref={chatInputRef}
          onSubmit={handleSend}
          onCancel={stop}
          disabled={disabled}
          isUploading={isUploading}
          isStreaming={isStreaming}
          isStopping={isStopping}
          enableAttachments={fileUploadEnabled}
          selectedFiles={fileUploadEnabled ? selectedFiles : undefined}
          onFilesChange={fileUploadEnabled ? onFilesChange : undefined}
          committedDocuments={committedDocuments}
          onAttachClick={handleAttachClick}
          inputSuggestion={inputSuggestion}
          composerSeed={composerSeed}
          onComposerSeedConsumed={onComposerSeedConsumed}
          textareaProps={{
            placeholder: defaultPlaceholder,
            minRows: 2,
            maxRows: 6
          }}
        />
        <Center>
          <Group gap="xs" mt={7} align="center">
            <Text c="dimmed" size="xs">
              AI can make mistakes. Verify important information.
            </Text>
          </Group>
        </Center>
      </Box>
    </Stack>
  );
}
